import { Button, InputNumber, Select, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { API } from 'service/api-route';
import { colors } from 'theme/tokens';
import UserPicker from 'pages/star-shared/components/user-picker';
import { useFeed } from './shared/context';
import { LogDetail, LogSummary, LogVerdict, StagePill } from './shared/components';
import { LOG_ACTION_LABEL, LOG_VERDICT_OPTIONS, STAGE_LABEL, TARGET_TYPE_LABEL, VERDICT_STYLE } from './shared/labels';
import { FeedLogRow } from './shared/types';
import { fmtDate, fmtTime, personText, useFeedList, useUrlFilters } from './shared/utils';
import styles from './styles.module.scss';

const PAGE_SIZE = 50;

const TARGET_OPTIONS = ['POST', 'COMMENT', 'MEDIA', 'USER', 'REPORT', 'SETTINGS', 'WORDS', 'TERMS'].map((value) => ({
  value,
  label: TARGET_TYPE_LABEL[value] || value,
}));

const STAGE_OPTIONS = Object.keys(STAGE_LABEL).map((value) => ({ value, label: STAGE_LABEL[value] }));
const VERDICT_OPTIONS = LOG_VERDICT_OPTIONS.map((value) => ({ value, label: VERDICT_STYLE[value]?.label || value }));

const pickerName = (label?: unknown) => (label ? String(label).split(' — ')[0] : undefined);

/** Ô "Đối tượng": Bài #123 (bấm mở ngăn chi tiết) / Bình luận #45 / Tài khoản {tên} / Cài đặt… */
const TargetCell = ({ row }: { row: FeedLogRow }) => {
  const { openPost } = useFeed();
  switch (row.targetType) {
    case 'POST':
      return row.targetId ? (
        <Typography.Link onClick={() => openPost(row.targetId)}>Bài #{row.targetId}</Typography.Link>
      ) : (
        <span>Bài viết (chưa lưu)</span>
      );
    case 'COMMENT':
      return row.postId ? (
        <Typography.Link onClick={() => openPost(row.postId as number, 'comments')}>
          Bình luận #{row.targetId}
        </Typography.Link>
      ) : (
        <span>Bình luận #{row.targetId}</span>
      );
    case 'MEDIA':
      return row.postId ? (
        <Typography.Link onClick={() => openPost(row.postId as number)}>Ảnh/video #{row.targetId}</Typography.Link>
      ) : (
        <span>Ảnh/video #{row.targetId}</span>
      );
    case 'USER':
      return <span>Tài khoản {personText(row.subject, row.subjectUserId || row.targetId)}</span>;
    case 'REPORT':
      return <span>Báo cáo #{row.targetId}</span>;
    default:
      return <span>{TARGET_TYPE_LABEL[row.targetType] || row.targetType}</span>;
  }
};

/** Tab "Nhật ký" (03 §10) — sổ chỉ đọc D20; IP và dữ liệu gốc máy soi chỉ ADMIN thấy. */
const LogsTab = () => {
  const { refreshKey } = useFeed();
  const { get, set, clear } = useUrlFilters();
  const page = Number(get('page')) || 1;
  const actorId = get('actorUserId');
  const subjectId = get('subjectUserId');
  const postIdParam = get('postId');

  const { items, total, loading } = useFeedList<FeedLogRow>(
    API.feedAdminLogs,
    {
      targetType: get('targetType'),
      postId: postIdParam,
      stage: get('stage'),
      verdict: get('verdict'),
      actorUserId: actorId,
      subjectUserId: subjectId,
      page,
      limit: PAGE_SIZE,
    },
    { refreshKey }
  );

  const columns: ColumnsType<FeedLogRow> = [
    {
      title: 'Thời gian',
      key: 'time',
      width: 124,
      render: (_, r) => (
        <div style={{ fontSize: 13, lineHeight: '20px' }}>
          <div>{fmtDate(r.createdAt)}</div>
          <div style={{ color: colors.textSecondary }}>{fmtTime(r.createdAt, true)}</div>
        </div>
      ),
    },
    { title: 'Đối tượng', key: 'target', width: 132, render: (_, r) => <TargetCell row={r} /> },
    {
      title: 'Việc',
      key: 'action',
      width: 176,
      render: (_, r) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
          <StagePill stage={r.stage} />
          <span style={{ fontSize: 13 }}>{LOG_ACTION_LABEL[r.action] || r.action}</span>
        </div>
      ),
    },
    {
      title: 'Kết quả',
      key: 'verdict',
      width: 120,
      render: (_, r) => <LogVerdict verdict={r.verdict} score={r.score} />,
    },
    {
      title: 'Người làm',
      key: 'actor',
      width: 144,
      render: (_, r) =>
        r.actorUserId ? (
          <Typography.Text ellipsis style={{ maxWidth: 128 }}>
            {personText(r.actor, r.actorUserId)}
          </Typography.Text>
        ) : (
          <span style={{ color: colors.textSecondary }}>Máy</span>
        ),
    },
    {
      title: 'Chi tiết',
      key: 'detail',
      render: (_, r) => (
        <div style={{ minWidth: 240 }}>
          <LogSummary row={r} />
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className={styles.filters}>
        <Select
          allowClear
          placeholder="Đối tượng"
          style={{ width: 160 }}
          value={get('targetType')}
          options={TARGET_OPTIONS}
          onChange={(v) => set({ targetType: v })}
        />
        <InputNumber
          placeholder="Bài số"
          min={1}
          style={{ width: 120 }}
          value={postIdParam ? Number(postIdParam) : null}
          onChange={(v) => set({ postId: v || undefined })}
        />
        <Select
          allowClear
          placeholder="Bước"
          style={{ width: 150 }}
          value={get('stage')}
          options={STAGE_OPTIONS}
          onChange={(v) => set({ stage: v })}
        />
        <Select
          allowClear
          placeholder="Kết quả"
          style={{ width: 130 }}
          value={get('verdict')}
          options={VERDICT_OPTIONS}
          onChange={(v) => set({ verdict: v })}
        />
        <div style={{ width: 220 }}>
          <UserPicker
            size="middle"
            placeholder="Người làm"
            value={actorId ? { value: Number(actorId), label: get('actorName') || `#${actorId}` } : undefined}
            onChange={(v) => set({ actorUserId: v?.value, actorName: pickerName(v?.label) })}
          />
        </div>
        <div style={{ width: 220 }}>
          <UserPicker
            size="middle"
            placeholder="Người bị ảnh hưởng"
            value={subjectId ? { value: Number(subjectId), label: get('subjectName') || `#${subjectId}` } : undefined}
            onChange={(v) => set({ subjectUserId: v?.value, subjectName: pickerName(v?.label) })}
          />
        </div>
        <Button onClick={() => clear(['tab', 'post'])}>Xoá lọc</Button>
      </div>
      <div className="custom-table">
        <Table<FeedLogRow>
          rowKey="id"
          size="middle"
          loading={loading}
          columns={columns}
          dataSource={items}
          locale={{ emptyText: 'Chưa có dòng nhật ký nào.' }}
          scroll={items.length ? { x: 960 } : undefined}
          expandable={{ expandedRowRender: (r) => <LogDetail row={r} /> }}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            showSizeChanger: false,
            hideOnSinglePage: true,
            onChange: (p) => set({ page: p }),
          }}
        />
      </div>
    </div>
  );
};

export default LogsTab;
